import { scrollElementIntoView } from './scroll-element-to-top';

export const COMMENT_THREAD_PULSE_CLASS = 'comment-thread-pulse';

export type FocusCommentNotificationTargetOptions = {
  commentId: string;
  replyId?: string | null;
  focusReply?: boolean;
  root?: ParentNode;
};

function isElementHidden(element: HTMLElement): boolean {
  let current: HTMLElement | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return true;
    }
    current = current.parentElement;
  }
  return false;
}

export function findCommentThreadElement(
  commentId: string,
  root: ParentNode = document
): HTMLElement | null {
  const nodes = root.querySelectorAll(
    `[data-comment-thread-id="${CSS.escape(commentId)}"]`
  );
  const candidates: HTMLElement[] = [];
  for (const node of nodes) {
    if (node instanceof HTMLElement) {
      candidates.push(node);
    }
  }
  if (candidates.length === 0) {
    return null;
  }

  const withLayout = candidates.filter(
    (element) => !isElementHidden(element) && element.getClientRects().length > 0
  );
  if (withLayout.length > 0) {
    return withLayout[0] ?? null;
  }

  const visible = candidates.find((element) => !isElementHidden(element));
  return visible ?? candidates[0] ?? null;
}

function pulseElement(element: HTMLElement): void {
  element.classList.remove(COMMENT_THREAD_PULSE_CLASS);
  void element.offsetWidth;
  element.classList.add(COMMENT_THREAD_PULSE_CLASS);

  const removePulse = (): void => {
    element.classList.remove(COMMENT_THREAD_PULSE_CLASS);
    element.removeEventListener('animationend', removePulse);
  };

  element.addEventListener('animationend', removePulse);
}

export function focusCommentNotificationTarget(
  options: FocusCommentNotificationTargetOptions
): boolean {
  const { commentId, replyId = null, focusReply = false, root = document } = options;
  const thread = findCommentThreadElement(commentId, root);
  if (!thread) {
    return false;
  }

  let highlightTarget: HTMLElement = thread;
  if (replyId) {
    const reply = thread.querySelector(
      `[data-comment-reply-id="${CSS.escape(replyId)}"]`
    );
    if (reply instanceof HTMLElement) {
      highlightTarget = reply;
    }
  }

  scrollElementIntoView(highlightTarget, { behavior: 'auto', block: 'center' });
  pulseElement(highlightTarget);

  if (focusReply) {
    const replyField = thread.querySelector(
      '[data-comment-reply-field] .ProseMirror'
    );
    if (replyField instanceof HTMLElement) {
      replyField.focus();
    }
  }

  return true;
}
